import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ToastProvider } from './components/Toast';

// Critical route - load immediately
import { Home } from './pages/Home';

// Lazy load other pages for better performance
const Services = lazy(() => import('./pages/Services').then(m => ({ default: m.Services })));
const Booking = lazy(() => import('./pages/Booking').then(m => ({ default: m.Booking })));
const Portfolio = lazy(() => import('./pages/Portfolio').then(m => ({ default: m.Portfolio })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const BlogPostPage = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPostPage })));
const Legal = lazy(() => import('./pages/Legal').then(m => ({ default: m.Legal })));
const Sitemap = lazy(() => import('./pages/Sitemap').then(m => ({ default: m.Sitemap })));
const ComplaintCenter = lazy(() => import('./pages/ComplaintCenter').then(m => ({ default: m.ComplaintCenter })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserDashboard = lazy(() => import('./pages/UserDashboard').then(m => ({ default: m.UserDashboard })));
const Testimonials = lazy(() => import('./pages/Testimonials').then(m => ({ default: m.Testimonials })));

// Admin protection wrapper
import { AdminProtectedRoute } from './components/AdminProtectedRoute';

// Loading fallback - minimal for fast perceived load
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-brand-lime border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Home loads immediately for fast first paint */}
              <Route path="/" element={<Home />} />

              {/* All other routes are lazy loaded */}
              <Route path="/services" element={<Services />} />
              <Route path="/services/*" element={<Services />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPostPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/testimonials" element={<Testimonials />} />

              {/* Legal Routes */}
              <Route path="/legal/:slug" element={<Legal />} />

              {/* System Routes */}
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/complaint-center" element={<ComplaintCenter />} />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } />
              <Route path="/dashboard" element={<UserDashboard />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Layout>
      </BrowserRouter>
    </ToastProvider>
  );
};

export default App;
